import { parseISO } from 'date-fns';
import { useLocale } from '../stores/userPreferencesStore';

export function useFormatDate() {
  const locale = useLocale();

  /**
   * Formats a bare date-only string (no time component — e.g. a Salesforce `Date` field, always
   * `yyyy-MM-dd`) using the active locale's numeric date order. Parses via `parseISO`, which
   * treats a date-only string as local midnight rather than a UTC instant — a calendar date has
   * no time-of-day component to convert through a timezone.
   */
  const formatDateOnly = (dateOnly: string): string =>
    new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(parseISO(dateOnly));

  /**
   * The same bare-date parsing as `formatDateOnly`, rendered with a spelled-out month ("Aug 3, 2026"
   * in en-US) for surfaces that read as prose rather than a form field.
   */
  const formatDateMedium = (dateOnly: string): string =>
    new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(parseISO(dateOnly));

  /** The same bare-date parsing, led by the weekday ("Friday, Aug 21, 2026" in en-US), for dated news items. */
  const formatDateWithWeekday = (dateOnly: string): string =>
    new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }).format(
      parseISO(dateOnly),
    );

  return { formatDateOnly, formatDateMedium, formatDateWithWeekday };
}
